import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ExclamationCircleIcon,
  ClockIcon,
  MapPinIcon,
  TagIcon,
} from "@heroicons/react/24/solid";
import { MapPinIcon as MapPinIconOutline } from "@heroicons/react/24/outline";
import Button from "../layout/Button";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { translateText } from "../../utils/translation";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import { useSearchParams } from "react-router-dom";
import Filters from "../layout/Filters";
import Card from "../layout/Card";
import {
  PointOfInterestState,
  createGlobalDirectionsToPoi,
  createUpdateLocalDirectionsToPoi,
  getDirections,
  getPointsOfInterest,
  updateGlobalDirectionsToPoi,
} from "../../slices/orientation";
import { getRooms } from "../../slices/rooms";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";

export default function Directions() {
  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();
  const searchCategory = searchParams.get("category");
  const searchType = searchParams.get("type");

  // Fetch data from server
  useEffect(() => {
    dispatch(
      getPointsOfInterest({ category: searchCategory, type: searchType }),
    );
    dispatch(getDirections({ category: searchCategory, type: searchType }));
    dispatch(getRooms());
  }, [dispatch, searchCategory, searchType]);

  // Set state
  const [category, setCategory] = useState({ value: "" });
  const [selectedPoi, setSelectedPoi] = useState<{
    id: string;
    name: { [key: string]: string };
  } | null>(null);

  const [showEditGlobalModal, setShowEditGlobalModal] =
    useState<boolean>(false);
  const [showEditLocalModal, setShowEditLocalModal] = useState<boolean>(false);
  const [selectedGlobalDirections, setSelectedGlobalDirections] = useState<{
    value: { [key: string]: string };
  }>({ value: { EN: "", FR: "", IT: "", DE: "" } });
  const [selectedLocalDirections, setSelectedLocalDirections] = useState<
    Array<{
      room_id: string;
      room_name: string;
      directions: { [key: string]: string };
    }>
  >([]);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const {
    points_of_interest: pointsOfInterest,
    directions,
    options,
  } = useAppSelector((state) => state.orientation);
  const { rooms } = useAppSelector((state) => state.rooms);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectPoiToEditGlobalDirections(e: any, args: any[]) {
    e.preventDefault();

    // Get poi data
    const [poiId] = args;
    const poiData = poisData.filter((poi) => poi.id === poiId)[0];
    setSelectedPoi({ id: poiId, name: poiData.name });
    setSelectedGlobalDirections({ value: { EN: "", FR: "", IT: "", DE: "" } });
    setShowEditLocalModal(false);

    // Get directions data
    if (directionsData === null) {
      setShowEditGlobalModal(true);
      return;
    }
    let directionData: any = directionsData.points_of_interest.filter(
      (x) => x.point_of_interest.id === poiId,
    );
    if (directionData.length === 0) {
      setShowEditGlobalModal(true);
      return;
    }
    [directionData] = directionData;

    const chosenDirections = directionData.directions.global;
    if (chosenDirections === null) {
      setShowEditGlobalModal(true);
      return;
    }
    setSelectedGlobalDirections({ value: chosenDirections.directions });
    setShowEditGlobalModal(true);
  }

  async function confirmEditGlobalDirections(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }

    // Validate selected POI
    if (selectedPoi === null) {
      dispatch(
        addAlert(generateAlertText("missing_poi", language), "danger", 5000),
      );
      return;
    }
    const { id } = selectedPoi;

    // Validate global directions
    const { value } = selectedGlobalDirections;

    // Check to see if global directions already exist
    let globalDirectionsId = null;
    if (directionsData !== null) {
      let check: any = directionsData.points_of_interest.filter(
        (x) => x.point_of_interest.id === id,
      );
      if (check.length > 0) check = check[0];
      if (check.directions.global !== null)
        globalDirectionsId = check.directions.global.id;
    }

    setShowEditGlobalModal(false);

    if (globalDirectionsId === null) {
      const res: any = await dispatch(
        createGlobalDirectionsToPoi({
          property: user.property.id,
          point_of_interest: id,
          directions: value,
        }),
      );
      if (res.payload.status === 201) {
        dispatch(
          addAlert(
            generateAlertText("create_global_directions", language),
            "success",
            3000,
          ),
        );
      } else {
        dispatch(
          addAlert(generateAlertText("create_error", language), "danger", 3000),
        );
        setShowEditGlobalModal(true);
      }
    } else {
      const res: any = await dispatch(
        updateGlobalDirectionsToPoi({
          property: user.property.id,
          point_of_interest: id,
          directionsId: globalDirectionsId,
          directions: value,
        }),
      );
      if (res.payload.status === 200) {
        dispatch(
          addAlert(
            generateAlertText("update_global_directions", language),
            "success",
            3000,
          ),
        );
      } else {
        dispatch(
          addAlert(generateAlertText("create_error", language), "danger", 3000),
        );
        setShowEditGlobalModal(true);
      }
    }
  }

  function selectPoiToEditLocalDirections(e: any, args: any[]) {
    e.preventDefault();

    // Get poi data
    const [poiId] = args;
    const poiData = poisData.filter((poi) => poi.id === poiId)[0];
    setSelectedPoi({ id: poiId, name: poiData.name });
    setShowEditGlobalModal(false);

    const directionsToPoi = directionsData?.points_of_interest.filter(
      (poi) => poi.point_of_interest.id === poiId,
    )[0];
    if (directionsToPoi === undefined) {
      setSelectedLocalDirections(
        roomsData.map((room: any) => ({
          room_id: room.id,
          room_name: room.name,
          directions: { EN: "", FR: "", IT: "", DE: "" },
        })),
      );
      setShowEditLocalModal(true);
      return;
    }
    if (directionsToPoi.directions.local === null) {
      setSelectedLocalDirections(
        roomsData.map((room: any) => ({
          room_id: room.id,
          room_name: room.name,
          directions: { EN: "", FR: "", IT: "", DE: "" },
        })),
      );
      setShowEditLocalModal(true);
      return;
    }
    const { local } = directionsToPoi.directions;
    let updatedLocalDirections: any = [];
    for (let i = 0; i < roomsData.length; i++) {
      const roomId = roomsData[i].id;
      const check = local.filter((x: any) => x.room.id === roomId);
      if (check.length === 0) {
        updatedLocalDirections.push({
          room_id: roomId,
          room_name: roomsData[i].name,
          directions: { EN: "", FR: "", IT: "", DE: "" },
        });
      } else {
        updatedLocalDirections.push({
          room_id: roomId,
          room_name: roomsData[i].name,
          directions: check[0].directions,
        });
      }
    }
    setSelectedLocalDirections(updatedLocalDirections);
    setShowEditLocalModal(true);
  }

  async function confirmEditLocalDirections(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }

    // Validate selected POI
    if (selectedPoi === null) {
      dispatch(
        addAlert(generateAlertText("missing_poi", language), "danger", 5000),
      );
      return;
    }
    const { id } = selectedPoi;

    const updatedLocalDirections = selectedLocalDirections.filter(
      (x) =>
        Object.values(x.directions).filter((y) => y.length > 0).length <
        Object.keys(x.directions).length,
    );

    setShowEditLocalModal(false);
    const res: any = await dispatch(
      createUpdateLocalDirectionsToPoi({
        property: user.property.id,
        poiId: id,
        directions: updatedLocalDirections,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("updated_local_directions", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditLocalModal(true);
    }
  }

  function updateLocalDirections(
    e: any,
    args: any[],
    selectedLanguage: string,
  ) {
    e.preventDefault();
    const [room] = args;
    const updatedRoom = {
      room_id: room.room_id,
      room_name: room.room_name,
      directions: { ...room.directions, [selectedLanguage]: e.target.value },
    };
    const updatedLocalDirections = selectedLocalDirections.map((x) =>
      x.room_id === room.room_id ? updatedRoom : x,
    );
    setSelectedLocalDirections(updatedLocalDirections);
  }

  function getEditLocalDirections(args: any[], selectedLanguage: string) {
    const [room] = args;
    return room.directions[selectedLanguage];
  }

  // Format data and filter by query param tag
  const roomsData = rooms.data === null ? [] : rooms.data;
  const poisData = pointsOfInterest.data === null ? [] : pointsOfInterest.data;
  const directionsData = directions.data === null ? null : directions.data;
  const poiCategory =
    options.category === undefined
      ? []
      : Object.keys(options.category).map((key) => ({
          value: key,
          label: options.category[key][language],
        }));

  return (
    <Template>
      {/* Header */}
      <Header text={translateText("directions_title", language)} />

      {/* Edit global directions modal */}
      <Modal
        show={showEditGlobalModal}
        setShow={setShowEditGlobalModal}
        confirmText={translateText("update_global_directions", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditGlobalDirections}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {`${translateText("edit_global_directions", language)}${
                selectedPoi !== null ? ` "${selectedPoi.name[language]}"` : ""
              }`}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Directions */}
              <TextInputMultiLanguage
                id="value"
                type="text"
                label={translateText("directions", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedGlobalDirections}
                placeholder={`${translateText(
                  "directions_placeholder",
                  language,
                )} ${
                  selectedPoi !== null ? ` "${selectedPoi.name[language]}"` : ""
                }`}
                stateName="value"
                onChange={setSelectedGlobalDirections}
                defaultLanguage={language}
                languageChoices={
                  poisData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(poisData[0].name)
                }
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit local directions modal */}
      <Modal
        show={showEditLocalModal}
        setShow={setShowEditLocalModal}
        confirmText={translateText("update_local_directions", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditLocalDirections}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {`${translateText("edit_local_directions", language)}${
                selectedPoi !== null ? ` "${selectedPoi.name[language]}"` : ""
              }`}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Array of rooms */}
              {selectedLocalDirections.map(
                (room: {
                  room_id: string;
                  room_name: string;
                  directions: { [key: string]: string };
                }) => (
                  <div key={room.room_id}>
                    <label
                      htmlFor={`local-directions-room-${room.room_id}`}
                      className={`mb-3 block text-left text-sm font-medium text-black`}
                    >
                      {room.room_name}
                    </label>

                    {/* <input
                      id={`local-directions-room-${room.room_id}`}
                      type="text"
                      className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                      placeholder={`${translateText(
                        "local_directions_placeholder",
                        language,
                      )} ${room.room_name}`}
                      value={room.directions}
                      required={false}
                      onChange={(e) => {
                        const updatedRoom = {
                          room_id: room.room_id,
                          room_name: room.room_name,
                          directions: e.target.value,
                        };
                        const updatedLocalDirections =
                          selectedLocalDirections.map((x) =>
                            x.room_id === room.room_id ? updatedRoom : x,
                          );
                        setSelectedLocalDirections(updatedLocalDirections);
                      }}
                    /> */}
                    <TextInputMultiLanguage
                      id={`local-directions-room-${room.room_id}`}
                      type="text"
                      label=""
                      className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                      placeholder={`${translateText(
                        "local_directions_placeholder",
                        language,
                      )} ${room.room_name}`}
                      required={false}
                      showLabel={false}
                      state={selectedLocalDirections}
                      stateName=""
                      valueHandler={getEditLocalDirections}
                      valueHandlerArgs={[room]}
                      onChangeHandler={updateLocalDirections}
                      onChangeHandlerArgs={[room]}
                      defaultLanguage={language}
                      languageChoices={
                        poisData.length === 0
                          ? ["EN", "FR", "IT", "DE"]
                          : Object.keys(poisData[0].name)
                      }
                    />
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {pointsOfInterest.loading || directions.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "category",
                  name: "Category",
                  options: poiCategory,
                },
              ]}
              queryParams={[["category", searchCategory]]}
              handlers={{
                category: {
                  state: category,
                  stateName: "value",
                  onChange: setCategory,
                },
              }}
              loading={pointsOfInterest.loading}
              baseUrl="/orientation/directions"
              showDateRange={false}
              showFilters={true}
              showSearchBar={false}
            />
            {poisData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {/* Point of interest data */}
                  {poisData.map((poi: PointOfInterestState) => (
                    <Card
                      key={poi.id}
                      colour="white"
                      className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset relative`}
                    >
                      <div className={`border-b border-light pb-2`}>
                        <div className="items-center justify-between">
                          <div className="flex">
                            <h3
                              className={`flex-grow text-base font-semibold leading-6 text-black truncate`}
                            >
                              {poi.name[language]}
                            </h3>
                            <div>
                              <Button
                                type="button"
                                className={`group inline-flex justify-center rounded-full text-sm bg-transparent`}
                                onClick={selectPoiToEditLocalDirections}
                                onClickArgs={[poi.id]}
                              >
                                <MapPinIconOutline
                                  className={`h-6 w-6 text-secondary group-hover:text-black`}
                                  aria-hidden="true"
                                />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <a
                        href="#!"
                        key={poi.id}
                        onClick={(e) =>
                          selectPoiToEditGlobalDirections(e, [poi.id])
                        }
                      >
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <MapPinIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {poi.category[1]}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <TagIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            <span className="truncate">{poi.type[1]}</span>
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <ClockIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {poi.datetime_created}
                          </div>
                        </div>
                      </a>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_pois", language)}
                subtext={translateText("no_pois_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
